import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useResizeObserver } from 'ovpn-ui-toolkit';

import { Message, NotificationBanner } from './PortalNotification.styles';
import type { Notification } from './PortalNotification.types';
import {
    decodeBase64,
    getNotification,
    setNotification,
    setTopOffset,
} from './PortalNotification.utils';

export function PortalNotification({
    message,
    alwaysDisplayed,
    sessionId,
}: Notification & { sessionId: string }) {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setVisible] = useState(false);
    const id = useMemo(() => decodeBase64(message + sessionId), [message, sessionId]);

    useEffect(() => {
        const hiddenNotificationId = getNotification();
        setVisible(id !== hiddenNotificationId);
    }, [id]);

    useResizeObserver(ref, setTopOffset, alwaysDisplayed || isVisible);

    const handleClose = useCallback(() => {
        setNotification(id);
        setVisible(false);
        setTopOffset(0);
    }, [id]);

    if (!isVisible && !alwaysDisplayed) {
        return null;
    }

    return (
        <div ref={ref} style={{ display: 'flex' }} id="portal-notification">
            <NotificationBanner
                appearance="attention"
                icon="info-circle"
                title={
                    <Message
                        id="portal-notification-content"
                        // biome-ignore lint/security/noDangerouslySetInnerHtml: intended
                        dangerouslySetInnerHTML={{
                            __html: message,
                        }}
                    />
                }
                onClose={alwaysDisplayed ? undefined : handleClose}
            />
        </div>
    );
}
